import React, { useState } from "react";
import {
  supportTicketService,
  SupportTicket,
} from "@core/services/SupportTicketService";
import { Plus, Calendar, AlertTriangle, TestTube } from "lucide-react";

interface TestTicketCreatorProps {
  onTicketCreated: (ticket: SupportTicket) => void;
}

export const TestTicketCreator: React.FC<TestTicketCreatorProps> = ({
  onTicketCreated,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    email: "test@example.com",
    subject: "",
    severity: "normal" as "low" | "normal" | "high" | "critical" | "blocker",
    daysAgo: 3, // Par défaut 3 jours dans le passé
    priority: 1,
  });

  const severityOptions = [
    { value: "low", label: "Low", color: "bg-gray-100 text-gray-800" },
    { value: "normal", label: "Normal", color: "bg-blue-100 text-blue-800" },
    { value: "high", label: "High", color: "bg-orange-100 text-orange-800" },
    { value: "critical", label: "Critical", color: "bg-red-100 text-red-800" },
    {
      value: "blocker",
      label: "Blocker",
      color: "bg-purple-100 text-purple-800",
    },
  ];

  const getSLADays = (severity: string): number => {
    switch (severity) {
      case "blocker":
        return 1;
      case "critical":
        return 2;
      case "normal":
        return 5;
      case "low":
        return 10;
      default:
        return 5;
    }
  };

  const getOverdueStatus = (daysAgo: number, severity: string) => {
    const slaDays = getSLADays(severity);
    if (daysAgo > slaDays) {
      return {
        isOverdue: true,
        overdueDays: daysAgo - slaDays,
        status: "🚨 OVERDUE",
      };
    }
    return {
      isOverdue: false,
      overdueDays: 0,
      status: "✅ Within SLA",
    };
  };

  const overdueStatus = getOverdueStatus(formData.daysAgo, formData.severity);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.subject.trim() || isCreating) return;

    setIsCreating(true);
    try {
      // Calculer la date dans le passé
      const createdDate = new Date();
      createdDate.setDate(createdDate.getDate() - formData.daysAgo);

      // Créer le ticket avec la date personnalisée
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/support_tickets`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email: formData.email,
            subject: formData.subject,
            severity: formData.severity,
            status: "open",
            priority: formData.priority,
            created_at: createdDate.toISOString(),
            updated_at: createdDate.toISOString(),
            metadata: {
              is_test_ticket: true,
              created_for_sla_testing: true,
              original_created_at: createdDate.toISOString(),
            },
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const responseText = await response.text();

      if (!responseText.trim()) {
        throw new Error("Empty response from server");
      }

      const ticket = JSON.parse(responseText);
      onTicketCreated(ticket);

      // Reset form
      setFormData({
        email: "test@example.com",
        subject: "",
        severity: "normal",
        daysAgo: 3,
        priority: 1,
      });
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to create test ticket:", error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
      >
        <TestTube className="w-4 h-4" />
        Créer ticket test SLA
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <TestTube className="w-5 h-5 text-purple-500" />
            Créer Ticket Test SLA
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-200"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="test@example.com"
            />
          </div>

          {/* Sujet */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Sujet *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Sujet du ticket de test"
              required
            />
          </div>

          {/* Sévérité */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Sévérité
            </label>
            <select
              value={formData.severity}
              onChange={(e) =>
                setFormData({ ...formData, severity: e.target.value as any })
              }
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {severityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} (SLA: {getSLADays(option.value)} jours)
                </option>
              ))}
            </select>
          </div>

          {/* Date dans le passé */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Créé il y a (jours)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="30"
                value={formData.daysAgo}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    daysAgo: parseInt(e.target.value) || 1,
                  })
                }
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <Calendar className="w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* SLA Status Preview */}
          <div
            className={`p-3 rounded-lg border ${
              overdueStatus.isOverdue
                ? "bg-red-900/20 border-red-800"
                : "bg-green-900/20 border-green-800"
            }`}
          >
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle
                className={`w-4 h-4 ${
                  overdueStatus.isOverdue ? "text-red-400" : "text-green-400"
                }`}
              />
              <span
                className={`font-medium ${
                  overdueStatus.isOverdue ? "text-red-300" : "text-green-300"
                }`}
              >
                {overdueStatus.status}
              </span>
            </div>
            {overdueStatus.isOverdue && (
              <div className="text-xs text-red-400 mt-1">
                Overdue by {overdueStatus.overdueDays} days
              </div>
            )}
            <div className="text-xs text-gray-400 mt-1">
              SLA: {getSLADays(formData.severity)} jours | Date:{" "}
              {new Date(
                Date.now() - formData.daysAgo * 24 * 60 * 60 * 1000,
              ).toLocaleDateString()}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!formData.subject.trim() || isCreating}
              className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? "Création..." : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
